import { jobsApi, ApiError } from "../utils/api";

export default function useJobActions({ isAuthenticated, notify, loginWithRedirect, getAccessTokenSilently, jobs, setJobs, setSelectedJob, goFeed }) {

  const handleClaimJob = async (job) => {
    if (!isAuthenticated) { notify("⚠️ Þú þarft að skrá þig inn"); loginWithRedirect(); return; }
    try {
      notify("⏳ Tek að mér verkið...");
      await jobsApi.claim(job.id, getAccessTokenSilently);
      const updatedJob = { ...job, status: 'claimed', claimedBy: 'me' };
      setSelectedJob(updatedJob); setJobs(jobs.map(j => j.id === job.id ? updatedJob : j));
      notify("✅ Þú hefur tekið að þér verkið!");
    } catch (error) {
      console.error('Failed to claim job:', error);
      notify(error instanceof ApiError ? `❌ ${error.message}` : "❌ Ekki tókst að taka að verkinu");
    }
  };

  const handleDeleteJob = async (job) => {
    if (!isAuthenticated) { notify("⚠️ Þú þarft að skrá þig inn"); return; }
    if (!confirm("Ertu viss um að þú viljir eyða þessu verki?")) return;
    try {
      notify("⏳ Eyði verki...");
      await jobsApi.delete(job.id, getAccessTokenSilently);
      setJobs(jobs.filter(j => j.id !== job.id));
      notify("🗑️ Verk eytt"); goFeed();
    } catch (error) {
      console.error('Failed to delete job:', error);
      notify(error instanceof ApiError ? `❌ ${error.message}` : "❌ Ekki tókst að eyða verki");
    }
  };

  const handleReleaseJob = async (job) => {
    if (!isAuthenticated) { notify("⚠️ Þú þarft að skrá þig inn"); return; }
    if (!confirm("Ertu viss um að þú viljir losa þetta verk?")) return;
    try {
      notify("⏳ Losa verk...");
      await jobsApi.release(job.id, getAccessTokenSilently);
      const updatedJob = { ...job, status: 'open', claimedBy: null };
      setSelectedJob(updatedJob); setJobs(jobs.map(j => j.id === job.id ? updatedJob : j));
      notify("🔓 Verk losað — nú geta aðrir tekið það");
    } catch (error) {
      console.error('Failed to release job:', error);
      notify(error instanceof ApiError ? `❌ ${error.message}` : "❌ Ekki tókst að losa verk");
    }
  };

  const handleCreateAddon = async (jobId, addonData) => {
    if (!isAuthenticated) { notify("⚠️ Þú þarft að skrá þig inn"); loginWithRedirect(); return; }
    try {
      await jobsApi.createAddon(jobId, addonData, getAccessTokenSilently);
    } catch (error) {
      console.error('Failed to create addon:', error);
      throw error;
    }
  };

  const handleStartJob = async (job) => {
    if (!isAuthenticated) { notify("⚠️ Þú þarft að skrá þig inn"); return; }
    try {
      notify("⏳ Hefja verk...");
      await jobsApi.startJob(job.id, getAccessTokenSilently);
      const updatedJob = { ...job, status: 'in_progress' };
      setSelectedJob(updatedJob); setJobs(jobs.map(j => j.id === job.id ? updatedJob : j));
      notify("▶️ Verk hafið!");
    } catch (error) {
      console.error('Failed to start job:', error);
      notify(error instanceof ApiError ? `❌ ${error.message}` : "❌ Ekki tókst að hefja verk");
    }
  };

  const handleCompleteJob = async (job) => {
    if (!isAuthenticated) { notify("⚠️ Þú þarft að skrá þig inn"); return; }
    if (!confirm("Ertu viss um að verkinu sé lokið?")) return;
    try {
      notify("⏳ Ljúka verki...");
      await jobsApi.completeJob(job.id, getAccessTokenSilently);
      const updatedJob = { ...job, status: 'done' };
      setSelectedJob(updatedJob); setJobs(jobs.map(j => j.id === job.id ? updatedJob : j));
      notify("✅ Verki lokið!");
    } catch (error) {
      console.error('Failed to complete job:', error);
      notify(error instanceof ApiError ? `❌ ${error.message}` : "❌ Ekki tókst að ljúka verki");
    }
  };

  const handleSetPayment = async (job, paymentData) => {
    if (!isAuthenticated) { notify("⚠️ Þú þarft að skrá þig inn"); return; }
    try {
      notify("⏳ Vista greiðsluupplýsingar...");
      await jobsApi.setPayment(job.id, paymentData, getAccessTokenSilently);
      setSelectedJob({ ...job, ...paymentData });
      notify("💳 Greiðsluupplýsingar vistaðar!");
    } catch (error) {
      console.error('Failed to set payment:', error);
      notify(error instanceof ApiError ? `❌ ${error.message}` : "❌ Ekki tókst að vista greiðslu");
    }
  };

  const handleUploadPhoto = async (job, file, caption, photoType) => {
    if (!isAuthenticated) { notify("⚠️ Þú þarft að skrá þig inn"); return; }
    try {
      notify("⏳ Hleð upp mynd...");
      const result = await jobsApi.uploadPhoto(job.id, file, caption, photoType, getAccessTokenSilently);
      notify("📸 Mynd hlaðið upp!");
      return result;
    } catch (error) {
      console.error('Failed to upload photo:', error);
      notify(error instanceof ApiError ? `❌ ${error.message}` : "❌ Ekki tókst að hlaða upp mynd");
    }
  };

  const handleEditJob = async (job, updates) => {
    if (!isAuthenticated) { notify("⚠️ Þú þarft að skrá þig inn"); return; }
    try {
      notify("⏳ Vista breytingar...");
      await jobsApi.update(job.id, updates, getAccessTokenSilently);
      const updatedJob = { ...job, ...updates };
      setSelectedJob(updatedJob);
      setJobs(jobs.map(j => j.id === job.id ? updatedJob : j));
      notify("✅ Verk uppfært!");
    } catch (error) {
      console.error('Failed to edit job:', error);
      notify(error instanceof ApiError ? `❌ ${error.message}` : "❌ Ekki tókst að uppfæra verk");
    }
  };

  return {
    handleClaimJob, handleDeleteJob, handleReleaseJob, handleCreateAddon,
    handleStartJob, handleCompleteJob, handleSetPayment, handleUploadPhoto, handleEditJob,
  };
}
